import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

const LoginScreen = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Verificar se há mensagem de timeout
  const timeoutMessage = searchParams.get('timeout') === 'true' 
    ? 'Sua sessão expirou por inatividade. Faça login novamente.' 
    : searchParams.get('expired') === 'true'
    ? 'Seu período de teste expirou. Entre em contato para continuar usando a plataforma.'
    : location.state?.message;

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const redirectPath = user.role === 'admin' ? '/admin' : '/company';
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    if (e) e.preventDefault();

    // Validar dados do formulário
    if (!formData.email || !formData.password) {
      setError('Email e senha são obrigatórios');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Fazer login real com Firebase
      await login(formData.email, formData.password);
    } catch (err) {
      // Verificar se é erro de empresa desabilitada
      if (err.message && err.message.includes('empresa está desabilitada')) {
        setError(err.message);
      } else {
        setError('Email ou senha inválidos: ' + (err.message || ''));
      }
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center">
            <img 
              src="/logo1200.png" 
              alt="MeuFiel Logo" 
              className="h-full w-full object-contain"
            />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            MeuFiel
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sistema de Fidelidade
          </p>
        </div>

        {/* Mensagem de timeout */}
        {timeoutMessage && (
          <div className={`border rounded-lg p-4 ${
            timeoutMessage.includes('expirou') && timeoutMessage.includes('teste')
              ? 'bg-red-50 border-red-200'
              : 'bg-orange-50 border-orange-200'
          }`}>
            <div className="flex">
              <div className="ml-3">
                <div className={`text-sm ${
                  timeoutMessage.includes('expirou') && timeoutMessage.includes('teste')
                    ? 'text-red-700'
                    : 'text-orange-700'
                }`}>
                  <p>{timeoutMessage}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Formulário de Login */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Fazer Login
            </h3>
            <p className="text-gray-600 text-sm">
              Digite suas credenciais de acesso
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite seu email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite sua senha"
                required
              />
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={!formData.email || !formData.password || loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">ou</span>
            </div>
          </div>
          
          <div className="space-y-3">
            <Link
              to="/customer-auth"
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium block text-center"
            >
              Consultar Meus Pontos
            </Link>
            
            <Link
              to="/trial-registration"
              className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition-colors font-medium block text-center"
            >
              Teste Grátis por 5 Dias
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;